/**
 * Real-time Subscription Hooks
 *
 * Enterprise features:
 * - Auto-connect on mount
 * - Auto-cleanup on unmount
 * - Connection state tracking
 * - Fallback to polling when WebSocket unavailable
 * - Optimistic updates integration
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { realtimeManager, ConnectionState } from '../lib/RealtimeManager';

/**
 * Hook to track real-time connection state
 */
export function useConnectionState() {
  const [state, setState] = useState(realtimeManager.getState());

  useEffect(() => {
    const unsubscribe = realtimeManager.onStateChange(setState);
    return unsubscribe;
  }, []);

  return state;
}

/**
 * Hook to connect/disconnect from real-time server
 */
export function useRealtimeConnection() {
  const state = useConnectionState();

  const connect = useCallback(() => {
    realtimeManager.connect();
  }, []);

  const disconnect = useCallback(() => {
    realtimeManager.disconnect();
  }, []);

  return {
    state,
    isConnected: state === ConnectionState.CONNECTED,
    isConnecting: state === ConnectionState.CONNECTING || state === ConnectionState.RECONNECTING,
    isFailed: state === ConnectionState.FAILED,
    connect,
    disconnect,
  };
}

/**
 * Hook to subscribe to a real-time channel
 *
 * @param {string} channel - Channel to subscribe to
 * @param {function} callback - Callback for incoming messages
 * @param {object} options - Options
 */
export function useRealtimeSubscription(channel, callback, options = {}) {
  const {
    enabled = true,
    autoConnect = true,
    fallbackInterval = null, // Polling interval if WebSocket fails
  } = options;

  const callbackRef = useRef(callback);
  const state = useConnectionState();

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Auto-connect on mount
  useEffect(() => {
    if (enabled && autoConnect && state === ConnectionState.DISCONNECTED) {
      realtimeManager.connect();
    }
  }, [enabled, autoConnect, state]);

  // Subscribe to channel
  useEffect(() => {
    if (!enabled || !channel) return;

    const handler = (data) => {
      callbackRef.current?.(data);
    };

    const unsubscribe = realtimeManager.subscribe(channel, handler);
    return unsubscribe;
  }, [channel, enabled]);

  // Fallback polling when WebSocket fails
  useEffect(() => {
    if (!fallbackInterval || state !== ConnectionState.FAILED) return;

    const interval = setInterval(() => {
      // Trigger a refetch via callback with null data
      // This signals the component to refetch via HTTP
      callbackRef.current?.(null);
    }, fallbackInterval);

    return () => clearInterval(interval);
  }, [fallbackInterval, state]);

  return {
    isConnected: state === ConnectionState.CONNECTED,
    connectionState: state,
  };
}

/**
 * Hook for subscribing to activity events
 */
export function useActivityStream(callback, options = {}) {
  return useRealtimeSubscription('activity', callback, {
    fallbackInterval: 30000, // Poll every 30s if WebSocket fails
    ...options,
  });
}

/**
 * Hook for subscribing to user events
 */
export function useUserEvents(userId, callback, options = {}) {
  const channel = userId ? `users:${userId}` : null;
  return useRealtimeSubscription(channel, callback, options);
}

/**
 * Hook for subscribing to tenant events
 */
export function useTenantEvents(tenantId, callback, options = {}) {
  const channel = tenantId ? `tenants:${tenantId}` : null;
  return useRealtimeSubscription(channel, callback, options);
}

/**
 * Hook for subscribing to fraud alerts
 */
export function useFraudAlerts(callback, options = {}) {
  return useRealtimeSubscription('fraud:alerts', callback, {
    fallbackInterval: 15000, // More frequent polling for security
    ...options,
  });
}

/**
 * Hook for subscribing to system notifications
 */
export function useSystemNotifications(callback, options = {}) {
  return useRealtimeSubscription('system:notifications', callback, options);
}

/**
 * Hook for subscribing to analytics updates
 */
export function useAnalyticsStream(callback, options = {}) {
  return useRealtimeSubscription('analytics:realtime', callback, {
    fallbackInterval: 60000, // Poll every minute for analytics
    ...options,
  });
}

/**
 * Hook that combines real-time updates with data fetching
 * Updates local state when real-time events arrive
 */
export function useRealtimeQuery(channel, fetchFn, options = {}) {
  const {
    onData,
    mergeStrategy = 'prepend', // 'prepend', 'append', 'replace', 'custom'
    maxItems = 100,
    enabled = true,
  } = options;

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initial fetch
  useEffect(() => {
    if (!enabled) return;

    let mounted = true;

    const load = async () => {
      try {
        setIsLoading(true);
        const result = await fetchFn();
        if (mounted) {
          setData(Array.isArray(result) ? result : result.data || []);
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          setError(err.message);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [fetchFn, enabled]);

  // Handle real-time updates
  const handleRealtimeUpdate = useCallback((event) => {
    if (!event) {
      // Fallback polling - refetch
      fetchFn().then(result => {
        setData(Array.isArray(result) ? result : result.data || []);
      }).catch(console.error);
      return;
    }

    // Notify callback
    onData?.(event);

    // Merge new data
    setData(prev => {
      let next;

      switch (mergeStrategy) {
        case 'prepend':
          next = [event, ...prev].slice(0, maxItems);
          break;
        case 'append':
          next = [...prev, event].slice(-maxItems);
          break;
        case 'replace':
          next = prev.map(item => item.id === event.id ? event : item);
          break;
        case 'custom':
          next = onData?.(event, prev) || prev;
          break;
        default:
          next = prev;
      }

      return next;
    });
  }, [mergeStrategy, maxItems, onData, fetchFn]);

  // Subscribe to channel
  useRealtimeSubscription(channel, handleRealtimeUpdate, { enabled });

  return {
    data,
    isLoading,
    error,
    setData,
  };
}

export default {
  useConnectionState,
  useRealtimeConnection,
  useRealtimeSubscription,
  useActivityStream,
  useUserEvents,
  useTenantEvents,
  useFraudAlerts,
  useSystemNotifications,
  useAnalyticsStream,
  useRealtimeQuery,
};
